import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';

type Profile = {
  id: number; login: string; email: string | null; phone: string | null;
  firstName: string; lastName: string; bio: string | null;
  timezone: string; locale: string; hasPassword: boolean;
  excludeNonEssentialEmails: boolean; typeName: string;
};

const LANGUAGES: Record<string, string> = { en: 'English', ar: 'العربية', id: 'Bahasa Indonesia' };

/**
 * Your own account.
 *
 * Contact details sit in their own section because changing them needs the
 * password — they are how the account is recovered and how WhatsApp reaches
 * somebody, and mixing them into the same Save button as "change my bio"
 * would mean asking for a password to edit a bio.
 */
export function Profile() {
  const [data, setData] = useState<{ profile: Profile; timezones: string[]; locales: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setData(await api.get('/profile')); }
    catch (e) { setError((e as ApiError).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (error && !data) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const say = (m: string) => { setNote(m); setError(null); void load(); };

  return (
    <>
      <h1 className="h1">My profile</h1>
      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      <About profile={data.profile} timezones={data.timezones} onSaved={say} onError={setError} />
      <Contact profile={data.profile} onSaved={say} onError={setError} />
      <Password hasPassword={data.profile.hasPassword} onSaved={say} onError={setError} />
    </>
  );
}

function About({ profile, timezones, onSaved, onError }: {
  profile: Profile; timezones: string[];
  onSaved: (m: string) => void; onError: (m: string) => void;
}) {
  const [form, setForm] = useState({
    firstName: profile.firstName, lastName: profile.lastName,
    bio: profile.bio ?? '', timezone: profile.timezone, locale: profile.locale,
    excludeNonEssentialEmails: profile.excludeNonEssentialEmails,
  });
  const [busy, setBusy] = useState(false);

  return (
    <section className="panel">
      <h2 className="h2">About you</h2>
      <div className="form">
        <label><span>Username</span>
          <input value={profile.login} disabled />
          <em className="muted small">Your username cannot be changed. Ask an administrator.</em>
        </label>
        <label><span>First name</span>
          <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></label>
        <label><span>Last name</span>
          <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></label>
        <label><span>About</span>
          <textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></label>
        <label><span>Language</span>
          <select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}>
            {Object.entries(LANGUAGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></label>
        <label><span>Time zone</span>
          <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
            {timezones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
          </select>
          <em className="muted small">Class times and deadlines are shown in this zone.</em>
        </label>
        <label className="check">
          <input type="checkbox" checked={form.excludeNonEssentialEmails}
            onChange={(e) => setForm({ ...form, excludeNonEssentialEmails: e.target.checked })} />
          Only send me messages I need — a class tomorrow, a deadline, a certificate running out
        </label>

        <button className="btn btn--primary" disabled={busy} onClick={async () => {
          setBusy(true);
          try {
            await api.patch('/profile', {
              firstName: form.firstName.trim(), lastName: form.lastName.trim(),
              bio: form.bio.trim() || null, timezone: form.timezone, locale: form.locale,
              excludeNonEssentialEmails: form.excludeNonEssentialEmails,
            });
            onSaved('Saved.');
          } catch (e) { onError((e as ApiError).message); }
          finally { setBusy(false); }
        }}>Save</button>
      </div>
    </section>
  );
}

function Contact({ profile, onSaved, onError }: {
  profile: Profile; onSaved: (m: string) => void; onError: (m: string) => void;
}) {
  const [email, setEmail] = useState(profile.email ?? '');
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const changed = (email.trim() || null) !== profile.email || (phone.trim() || null) !== profile.phone;

  // Changing either needs the password, so somebody without one is told
  // where to start rather than shown a form that can only refuse them.
  if (!profile.hasPassword) {
    return (
      <section className="panel">
        <h2 className="h2">How we reach you</h2>
        <p className="muted">
          Email {profile.email ?? 'not set'}{profile.phone ? `, phone ${profile.phone}` : ''}. Set a password
          below first — changing these needs it, because they are how your account is recovered.
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2 className="h2">How we reach you</h2>
      <p className="muted small">
        These are how your account is recovered and how class reminders reach you on WhatsApp,
        so changing either one needs your password.
      </p>
      <div className="form">
        <label><span>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label><span>Phone</span>
          <input value={phone} placeholder="+62…" onChange={(e) => setPhone(e.target.value)} />
          <em className="muted small">In international form, starting with the country code.</em>
        </label>

        {changed && (
          <label><span>Your password</span>
            <input type="password" value={password} autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)} /></label>
        )}

        <button className="btn btn--primary" disabled={busy || !changed || !password} onClick={async () => {
          setBusy(true);
          try {
            await api.patch('/profile', {
              email: email.trim() || null, phone: phone.trim() || null, currentPassword: password,
            });
            setPassword('');
            onSaved('Your contact details are updated.');
          } catch (e) { onError((e as ApiError).message); }
          finally { setBusy(false); }
        }}>Save contact details</button>
      </div>
    </section>
  );
}

/**
 * Change it — or set one, for somebody who has none yet. That is mostly a
 * member of staff still holding a session from UCC sign-in, which outlived
 * UCC itself; asking them for a current password they never had would leave
 * them with no way to get one except a link by email.
 */
function Password({ hasPassword, onSaved, onError }: {
  hasPassword: boolean; onSaved: (m: string) => void; onError: (m: string) => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);

  const mismatch = again.length > 0 && next !== again;

  return (
    <section className="panel">
      <h2 className="h2">{hasPassword ? 'Password' : 'Set a password'}</h2>
      <p className="muted small">
        {hasPassword
          ? 'Changing it signs you out on every other device, and leaves you signed in here.'
          : 'You do not have one yet. Choose one now and use it to sign in from here on.'}
      </p>
      <div className="form">
        {hasPassword && (
          <label><span>Current password</span>
            <input type="password" autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)} /></label>
        )}
        <label><span>New password</span>
          <input type="password" autoComplete="new-password"
            value={next} onChange={(e) => setNext(e.target.value)} />
          <em className="muted small">At least eight characters, with a capital, a small letter and a number.</em>
        </label>
        <label><span>New password again</span>
          <input type="password" autoComplete="new-password"
            value={again} onChange={(e) => setAgain(e.target.value)} />
          {mismatch && <em className="warn small">These two do not match.</em>}
        </label>

        <button className="btn btn--primary"
          disabled={busy || (hasPassword && !current) || !next || mismatch || !again}
          onClick={async () => {
            setBusy(true);
            try {
              await api.post('/auth/password', hasPassword
                ? { currentPassword: current, newPassword: next }
                : { newPassword: next });
              setCurrent(''); setNext(''); setAgain('');
              onSaved(hasPassword
                ? 'Your password is changed, and every other device is signed out.'
                : 'Your password is set. Use it to sign in from now on.');
            } catch (e) { onError((e as ApiError).message); }
            finally { setBusy(false); }
          }}>{hasPassword ? 'Change password' : 'Set password'}</button>
      </div>
    </section>
  );
}
